import React from 'react';
import { Grid, TextField, MenuItem, Button, Typography, Box, Tooltip, InputAdornment, IconButton } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { API } from '../../api/api';
import { END_POINTS } from '../../api/endpoints';
import { useToast } from '../../context/ToastContext';
import ReplayIcon from '@mui/icons-material/Replay';

const today = new Date();
const defaultStartingDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().split("T")[0];
const maxAllowedDate = new Date(today.getFullYear() - 8, today.getMonth(), today.getDate());

const validationSchema = Yup.object({
    firstName: Yup.string()
        .trim()
        .matches(/^[A-Za-z ]+$/, "Only alphabets allowed")
        .min(2, "Too short")
        .max(22, "Too long")
        .required("First Name is required"),
    lastName: Yup.string().required('Last Name is required'),
    birthDate: Yup.date()
        .nullable()
        .notRequired()
        .transform((curr, orig) => orig === '' ? null : curr)
        .max(maxAllowedDate, 'Age must be at least 8 years'),
    address: Yup.string().nullable().notRequired(),
    city: Yup.string().nullable().notRequired(),
    pincode: Yup
        .string("Enter your pincode")
        .nullable()
        .notRequired()
        .test("pincodeValidation", "Enter a valid pincode", function (value) {
            if (!value) return true;
            const pinRegex = /^\d{6}$/; // Change this regex based on requirement
            let isValidPin = pinRegex.test(value);
            if (!isValidPin) {
                return false;
            }
            return true;
        }),
    phone: Yup.string().matches(/^[0-9]{10}$/, 'Phone Number must be exactly 10 digits')
        .required('Contact is required'),
    maritalStatus: Yup.string().nullable().notRequired(),
    education: Yup.string().nullable().notRequired(),
    occupation: Yup.string().nullable().notRequired(),
    email: Yup.string()
        .trim()
        .email("Invalid email")
        .nullable()
        .notRequired(),
    emergencyLastName: Yup.string().nullable().notRequired(),
    emergencyAge: Yup.number()
        .typeError("Age must be a number")
        .integer("No decimals allowed")
        .min(18, "Minimum 18")
        .max(80, "Maximum 80")
        .nullable()
        .notRequired()
        .transform((curr, orig) => orig === '' ? null : curr),
    emergencyOccupation: Yup.string().nullable().notRequired(),
    emergencyPhone: Yup.string()
        .nullable()
        .notRequired()
        .test("emergencyPhoneValidation", "Phone must be 10 digits", function (value) {
            if (!value) return true;
            const phoneRegex = /^[0-9]{10}$/;
            return phoneRegex.test(value);
        }),
    emergencyFirstName: Yup.string().nullable().notRequired(),
    emergencyRelationship: Yup.string().nullable().notRequired(),
});

const helperStyle = {
    sx: {
        textAlign: 'center',
        marginLeft: 0,
        fontSize: '13px', fontWeight: '500'
    },
}

const motherRelations = ['Husband', 'Maternal grandmother', "Maternal grandfather", "Uncle", "Aunt", "Mother-in-law", "Father-in-law", "Sister-in-law", 'Mother', "Father","Others"];

const maritalStatus = [{ value: "MARRIED", label: "Married" }, { value: "NOT_MARRIED", label: "Not Married" }, { value: "DIVORCED", label: "Divorced" }, { value: "WIDOWED", label: "Widowed" }, { value: "OTHERS", label: 'Others' }];

const occupation = [{ value: "BUSINESS", label: 'Business' }, { value: "WORKING", label: 'Working' }, { value: "HOUSEWIFE", label: 'Housewife' }, { value: "STUDENT", label: 'Student' }, { value: "OTHERS", label: 'Others' }];

const patientPriorityOptions = [{ value: "NORMAL", label: "Normal" }, { value: "VIP", label: "VIP" }];

const husbandOccupation = [{ value: "BUSINESS", label: 'Business' }, { value: "WORKING", label: 'Working' }, { value: "OTHERS", label: 'Others' }];

const inputStyle = {
    sx: {
        width: '140px',
        height: '35px',
        padding: 0,
        borderRadius: '10px',
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        '& input': {
            textAlign: 'center',
            padding: '4px',
            fontSize: '14px',
            // fontWeight:'600'
        },
    },
}

const labelStyle = { textAlign: 'left', fontSize: '16px', fontWeight: '500' }


const PatientRegistrationForm = ({ handleClose, addPatient }) => {
    const showToast = useToast()
    const [otpSent, setOtpSent] = React.useState(false);
    const [otpVerified, setOtpVerified] = React.useState(false);
    const [otp, setOtp] = React.useState("");
    const [verificationChoice, setVerificationChoice] = React.useState(null);

    const createPatient = async (data) => {
        try {
            let baseUrl =
                process.env.NODE_ENV === 'development'
                    ? process.env.REACT_APP_DEVELOP
                    : process.env.REACT_APP_PROD;
            const patient = await API.post(`${baseUrl}${END_POINTS.createPatient}`, {
                "firstName": data.firstName,
                "lastName": data.lastName,
                "dateOfBirth": data.birthDate || null,
                "addressLine1": data.address || "",
                "city": data.city || "",
                "postalCode": data.pincode || "", // do it using api
                "countryCode": "+91",
                "phone": data.phone,
                "occupation": data.occupation || "",
                "education": data.education || "",
                "maritalStatus": data.maritalStatus ? data.maritalStatus.toUpperCase() : "",
                "priority": data.priority || "NORMAL",
                // "email": data.email??null, // 
                "careGiverInfo": {
                    "firstName": data.emergencyFirstName || "",
                    "lastName": data.emergencyLastName || "",
                    "age": data.emergencyAge ? parseInt(data.emergencyAge) : null,
                    "phone": data.emergencyPhone || "",
                    "occupation": data.emergencyOccupation || "",
                    "relation": data.emergencyRelationship || ""
                },
            })
            if (!patient.success) {
                throw new Error(patient.message);
            }
            addPatient(patient.data)
            handleClose()
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.errors?.error ||
                err.message ||
                "Something went wrong";

            console.error("Error:", message);
            showToast(message, "error");
        }
    };

    const fetchCityByPincode = (setFieldValue, pincode) => {
        fetch(`https://api.postalpincode.in/pincode/${pincode}`)
            .then((res) => res.json())
            .then((res) => {
                if (res[0].Status === "Error") {
                    throw new Error(res[0].Message);
                }
                setFieldValue("city", res[0].PostOffice[0].Block);
            })
            .catch((error) => {
                if (error.message === "Error: No records found") {
                    showToast("No record found", 'inof');
                }
                setFieldValue("city", "");
                setFieldValue("pincode", "");
            });
    };

    const sendOtp = async (phone) => {
        try {
            const signinResponse = await API.post(`/v1/patients/send-otp`, {
                phone,
                countryCode: "+91",
                platform: "ONI_MOM_MOBILE"
            });
            if (signinResponse.success) {
                setOtpSent(true);
                showToast("OTP sent successfully", "success");
            }
            else {
                throw new Error(signinResponse.message)
            }
        } catch (err) {
            showToast("Failed to send OTP", "error");
        }
    };

    const verifyOtp = async (phone) => {
        try {
            const verifyResponse = await API.post("/v1/patients/verify-otp", {
                phone,
                countryCode: "+91",
                platform: "ONI_MOM_MOBILE",
                otp
            });
            if (verifyResponse.success) {
                if (verifyResponse?.data?.isExistingPatient) {
                    showToast(`Patient already exist with name ${verifyResponse?.data?.firstName} ${verifyResponse?.data?.lastName}`, 'info')
                }
                else {
                    setOtpVerified(true);
                    showToast("OTP verified", "success");
                }
            }
            else {
                throw new Error(verifyResponse.message)
            }
        } catch (err) {
            showToast("Invalid OTP", "error");
        }
    };

    return (
        <Formik
            initialValues={{
                firstName: '',
                lastName: '',
                birthDate: '',
                address: '',
                city: '',
                maritalStatus: '',
                education: '',
                occupation: '',
                priority: 'NORMAL',
                email: '',
                emergencyRelationship: '',
                emergencyFirstName: '',
                emergencyLastName: '',
                emergencyOccupation: '',
                emergencyAge: '',
                emergencyPhone: '',
                phone: '',
                pincode: ''
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                console.log("printed", values);
                if (
                    verificationChoice === "NOW" &&
                    !otpVerified
                  ) {
                    showToast("Please verify phone number first", "error");
                    return;
                }
                createPatient(values)
            }}

        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue, }) => (
                <Form style={{ marginTop: '15px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>First Name*</Typography>
                            <Field
                                name="firstName"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/[^A-Za-z ]/g, "");
                                    value = value.replace(/\s+/g, " ");
                                    value = value.replace(/^\s+/g, "");

                                    setFieldValue("firstName", value);
                                }}
                                onBlur={(e) => {
                                    handleBlur(e);
                                    setFieldValue("firstName", e.target.value.trim());
                                }}
                                inputProps={{
                                    maxLength: 22,
                                }}
                                value={values.firstName}
                                error={touched.firstName && Boolean(errors.firstName)}
                                helperText={touched.firstName && errors.firstName}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>



                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Last Name*</Typography>
                            <Field
                                name="lastName"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onBlur={(e) => {
                                    handleBlur(e);
                                    setFieldValue("lastName", e.target.value.trim());
                                }}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/[^A-Za-z ]/g, "");
                                    value = value.replace(/\s+/g, " ");
                                    value = value.replace(/^\s+/g, "");

                                    setFieldValue("lastName", value);
                                }}
                                inputProps={{
                                    maxLength: 18,
                                }}
                                value={values.lastName}
                                error={touched.lastName && Boolean(errors.lastName)}
                                helperText={touched.lastName && errors.lastName}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Birth Date</Typography>
                            <Field
                                name="birthDate"
                                as={TextField}
                                fullWidth
                                type="date"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onClick={() => {
                                    if (!values.birthDate) {
                                        setFieldValue("birthDate", defaultStartingDate);
                                    }
                                }}
                                value={values.birthDate}
                                error={touched.birthDate && Boolean(errors.birthDate)}
                                helperText={touched.birthDate && errors.birthDate}
                                inputProps={{
                                    max: maxAllowedDate.toISOString().split("T")[0],
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Priority</Typography>
                            <Field
                                name="priority"
                                as={TextField}
                                fullWidth
                                select
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.priority}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={{
                                    ...inputStyle,
                                    style: {
                                        fontSize: '12px',
                                    },
                                }}
                            >
                                {patientPriorityOptions.map((item, i) => {
                                    return <MenuItem value={item.value} key={i}>{item.label}</MenuItem>
                                })}
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Address</Typography>
                            <Field
                                name="address"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.address}
                                error={touched.address && Boolean(errors.address)}
                                helperText={touched.address && errors.address}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>City</Typography>
                            <Field
                                name="city"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.city}
                                error={touched.city && Boolean(errors.city)}
                                helperText={touched.city && errors.city}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Pincode</Typography>
                            <Field
                                name="pincode"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/\D/g, "");
                                    if (value.length <= 6) {
                                        setFieldValue("pincode", value);
                                    }
                                    if (value.length === 6) {
                                        fetchCityByPincode(setFieldValue, value);
                                    }
                                }}
                                inputProps={{
                                    maxLength: 6,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*"
                                }}
                                onBlur={handleBlur}
                                value={values.pincode}
                                error={touched.pincode && Boolean(errors.pincode)}
                                helperText={touched.pincode && errors.pincode}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Marital Status</Typography>
                            <Field
                                name="maritalStatus"
                                as={TextField}
                                fullWidth
                                select
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.maritalStatus}
                                error={touched.maritalStatus && Boolean(errors.maritalStatus)}
                                helperText={touched.maritalStatus && errors.maritalStatus}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={{
                                    ...inputStyle,
                                    style: {
                                        fontSize: '12px',
                                    },
                                }}
                            >
                                {maritalStatus.map((item, i) => {
                                    return <MenuItem value={item.value} key={i}>{item.label}</MenuItem>
                                })}
                            </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Email</Typography>
                            <Field
                                name="email"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.email}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Education</Typography>
                            <Field
                                name="education"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.education}
                                error={touched.education && Boolean(errors.education)}
                                helperText={touched.education && errors.education}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Occupation</Typography>
                            <Field
                                name="occupation"
                                as={TextField}
                                fullWidth
                                select
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.occupation}
                                error={touched.occupation && Boolean(errors.occupation)}
                                helperText={touched.occupation && errors.occupation}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={{
                                    ...inputStyle,
                                    style: {
                                        fontSize: '12px',
                                    },
                                }}
                            >
                                {occupation.map((item, i) => {
                                    return <MenuItem value={item.value} key={i}>{item.label}</MenuItem>
                                })}
                            </Field>
                        </Grid>
                    </Grid>
                    <Box sx={{ width: '100%', pt: 1 }}>
                        <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Contact*</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ width: { xs: '100%', sm: '50%' }, maxWidth: '140px', flexShrink: 0 }}>
                                <Field
                                    name="phone"
                                    as={TextField}
                                    fullWidth
                                    placeholder="Type here"
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/\D/g, "");
                                        if (value.length <= 10) {
                                            setFieldValue("phone", value);
                                            setOtpSent(false);
                                            setOtpVerified(false);
                                            setOtp("");
                                            setVerificationChoice(null);
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    inputProps={{
                                        maxLength: 10,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*"
                                    }}
                                    value={values.phone}
                                    error={touched.phone && Boolean(errors.phone)}
                                    helperText={touched.phone && errors.phone}
                                    FormHelperTextProps={helperStyle}
                                    InputProps={{
                                        ...inputStyle,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {otpSent && (
                                                    <Tooltip title="Click here to resend the otp">
                                                        <IconButton onClick={() => sendOtp(values.phone)}>
                                                            <ReplayIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </InputAdornment>
                                        ),

                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                    }}
                                />
                            </Box>
                                  {values.phone.length === 10 &&
                                    !verificationChoice &&
                                    !otpVerified && (
                                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                        <Button
                                            variant='outlined'
                                            size="small"
                                            onClick={() => {
                                                setVerificationChoice("NOW");
                                                sendOtp(values.phone);
                                            }}
                                            sx={{
                                                height: { lg: '16px', xl: '25px' }, minWidth: '85px', fontSize: '11px'
                                            }}
                                        >
                                            Verify Now
                                        </Button>

                                        <Button
                                           variant='outlined'
                                           size="small"
                                            onClick={() => {
                                                setVerificationChoice("LATER");
                                            }}
                                            sx={{
                                                height: { lg: '16px', xl: '25px' }, minWidth: '90px', fontSize: '11px'
                                            }}
                                        >
                                            Verify Later
                                        </Button>
                                        </Box>
                                    )}
                                    
                                {otpVerified && (
                                    <Typography variant="body2" sx={[labelStyle, { ml: 1 }]}>
                                        ✅
                                    </Typography>
                                )}
                        </Box>
                        {verificationChoice === "NOW" && otpSent && !otpVerified && (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ width: { xs: '100%', sm: '50%' }, maxWidth: '140px', flexShrink: 0 }}>
                                        <Field
                                            name="Otp"
                                            as={TextField}
                                            fullWidth
                                            placeholder="Enter OTP"
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, "");
                                                if (value.length <= 6) setOtp(value);
                                            }}
                                            onBlur={handleBlur}
                                            inputProps={{
                                                maxLength: 6,
                                                inputMode: "numeric",
                                                pattern: "[0-9]*"
                                            }}
                                            value={otp}
                                            InputProps={inputStyle}
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Button
                                        size="small"
                                        sx={{ flexShrink: 0, height: { lg: '16px', xl: '25px' }, minWidth: '60px', fontSize: '11px' }}
                                        onClick={() => verifyOtp(values.phone)}
                                    >
                                        Verify
                                    </Button>
                                </Box>
                            </>
                        )}


                    </Box>
                    <Grid container spacing={2} mt={2}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" gutterBottom sx={{ textAlign: 'left', fontSize: '16px', color: '#272525', fontWeight: '600' }}>Emergency Contact</Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>First Name</Typography>
                            <Field
                                name="emergencyFirstName"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onBlur={(e) => {
                                    handleBlur(e);
                                    setFieldValue("emergencyFirstName", e.target.value.trim());
                                }}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/[^A-Za-z ]/g, "");
                                    value = value.replace(/\s+/g, " ");
                                    value = value.replace(/^\s+/g, "");

                                    setFieldValue("emergencyFirstName", value);
                                }}
                                inputProps={{
                                    maxLength: 22,
                                }}
                                value={values.emergencyFirstName}
                                error={touched.emergencyFirstName && Boolean(errors.emergencyFirstName)}
                                helperText={touched.emergencyFirstName && errors.emergencyFirstName}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Last Name</Typography>
                            <Field
                                name="emergencyLastName"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onBlur={(e) => {
                                    handleBlur(e);
                                    setFieldValue("emergencyLastName", e.target.value.trim());
                                }}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/[^A-Za-z ]/g, "");
                                    value = value.replace(/\s+/g, " ");
                                    value = value.replace(/^\s+/g, "");

                                    setFieldValue("emergencyLastName", value);
                                }}
                                inputProps={{
                                    maxLength: 18,
                                }}
                                value={values.emergencyLastName}
                                error={touched.emergencyLastName && Boolean(errors.emergencyLastName)}
                                helperText={touched.emergencyLastName && errors.emergencyLastName}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Phone No</Typography>

                            <Field
                                name="emergencyPhone"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/\D/g, "");
                                    if (value.length <= 10) {
                                        setFieldValue("emergencyPhone", value);
                                    }
                                }}
                                onBlur={handleBlur}
                                inputProps={{
                                    maxLength: 10,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*"
                                }}
                                value={values.emergencyPhone}
                                error={touched.emergencyPhone && Boolean(errors.emergencyPhone)}
                                helperText={touched.emergencyPhone && errors.emergencyPhone}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Age</Typography>

                            <Field
                                name="emergencyAge"
                                as={TextField}
                                fullWidth
                                placeholder="Type here"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/\D/g, "");
                                    if (value.length <= 2) {
                                        setFieldValue("emergencyAge", value);
                                    }
                                }}
                                onBlur={handleBlur}
                                inputProps={{
                                    maxLength: 2,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*"
                                }}
                                value={values.emergencyAge}
                                error={touched.emergencyAge && Boolean(errors.emergencyAge)}
                                helperText={touched.emergencyAge && errors.emergencyAge}
                                FormHelperTextProps={helperStyle}
                                InputProps={inputStyle}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>Occupation</Typography>
                            <Field
                                name="emergencyOccupation"
                                as={TextField}
                                fullWidth
                                select
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.emergencyOccupation}
                                error={touched.emergencyOccupation && Boolean(errors.emergencyOccupation)}
                                helperText={touched.emergencyOccupation && errors.emergencyOccupation}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                FormHelperTextProps={helperStyle}
                                InputProps={{
                                    ...inputStyle,
                                    style: {
                                        fontSize: '12px',
                                    },
                                }}
                            >
                                {husbandOccupation.map((item, i) => {
                                    return <MenuItem value={item.value} key={i}>{item.label}</MenuItem>
                                })}
                            </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom sx={labelStyle}>RelationShip</Typography>
                            <Field
                                name="emergencyRelationship"
                                as={TextField}
                                fullWidth
                                select
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.emergencyRelationship}
                                error={touched.emergencyRelationship && Boolean(errors.emergencyRelationship)}
                                helperText={touched.emergencyRelationship && errors.emergencyRelationship}
                                FormHelperTextProps={helperStyle}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                InputProps={{
                                    ...inputStyle,
                                    style: {
                                        fontSize: '12px',
                                    },
                                }}
                            >
                                {motherRelations.map((item, i) => {
                                    return <MenuItem value={item} key={i}>{item}</MenuItem>
                                })}
                            </Field>
                        </Grid>

                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignSelf: 'flex-end', mt: { xs: 2, md: 0 } }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                handleClose()
                            }}
                            sx={{ height: '27px', width: '78px', fontSize: '12px', backgroundColor: 'rgba(221, 238, 253, 1)', color: 'black', borderRadius: '30px', mr: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            sx={{ height: '27px', width: '78px', fontSize: '12px', backgroundColor: '#FFF', color: 'black', borderRadius: '30px' }}
                        >
                            Submit
                        </Button>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default PatientRegistrationForm;
